import React, { useState, useRef, useMemo } from 'react';
import {
  ArrowDownAZ,
  ArrowDownZA,
  ArrowDown01,
  ArrowDown10,
  Layout,
  X,
} from 'lucide-react';

const NoDataMessage = ({ message = "No Data Available" }) => (
  <div className="flex items-center justify-center h-40 w-full text-[#CCCCCC] text-lg font-medium">
    {message}
  </div>
);

const ReusableTable = ({
  columns,
  data,
  visibleColumns,
  onColumnToggle,
  onSelectAllColumns,
  onUnselectAllColumns,
  noDataMessage = "No Data Available",
  truncateLength = 25,
  onRowClick,
  rowClassName = "",
  sortConfig,
  onSort,
}) => {
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  const [internalSortConfig, setInternalSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const activeSortConfig = sortConfig !== undefined ? sortConfig : internalSortConfig;
  const setActiveSortConfig = onSort || setInternalSortConfig;

  const columnSelectorRef = useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(e.target)) {
        setShowColumnSelector(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sortedData = useMemo(() => {
    if (!activeSortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[activeSortConfig.key];
      let bValue = b[activeSortConfig.key];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const isDate = !isNaN(Date.parse(aValue));
      const isNumber = !isNaN(aValue) && aValue !== "";

      if (isDate) {
        const dateA = new Date(aValue).getTime();
        const dateB = new Date(bValue).getTime();
        return activeSortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      if (isNumber) {
        return activeSortConfig.direction === "asc"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return activeSortConfig.direction === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [data, activeSortConfig]);

  const requestSort = (key) => {
    const column = columns.find((c) => c.key === key);
    if (!column?.sortable) return;

    let direction = "asc";
    if (activeSortConfig.key === key && activeSortConfig.direction === "asc") {
      direction = "desc";
    }
    setActiveSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (activeSortConfig.key !== key) return null;

    const isDate = key === "created_at" || key === "end_date";
    const isNumber = key.includes("id") || key.includes("count") || key.includes("allocated") || key.includes("total");

    if (isDate || isNumber) {
      return activeSortConfig.direction === "asc" ? (
        <ArrowDown01 className="w-4 h-4 text-white" />
      ) : (
        <ArrowDown10 className="w-4 h-4 text-white" />
      );
    }

    return activeSortConfig.direction === "asc" ? (
      <ArrowDownAZ className="w-4 h-4 text-white" />
    ) : (
      <ArrowDownZA className="w-4 h-4 text-white" />
    );
  };

  const visibleColumnCount = Object.values(visibleColumns || {}).filter(Boolean).length;

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse">
        <thead>
          <tr className="border-b border-[#5A4479] text-sm font-medium text-[#D3B4FF]">
            {columns.map(({ key, label, sortable = false, width }) =>
              visibleColumns[key] && (
                <th
                  key={key}
                  className={`text-left py-4 px-6 font-medium select-none truncate ${
                    sortable ? 'cursor-pointer hover:opacity-80' : ''
                  }`}
                  style={{ width: width || 'auto' }}
                  onClick={() => sortable && requestSort(key)}
                >
                  <div className="flex items-center gap-2 truncate">
                    <span className="truncate">{label}</span>
                    {sortable && getSortIcon(key)}
                  </div>
                </th>
              )
            )}
          </tr>
        </thead>

        <tbody className="text-gray-300 text-sm font-light">
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={visibleColumnCount || columns.length} className="text-center py-20">
                <NoDataMessage message={noDataMessage} />
              </td>
            </tr>
          ) : (
            sortedData.map((row, index) => (
              // <tr
              //   key={row.organization_id || row.id || index}
              //   className={`border-b border-[#5A4479]/30 hover:bg-[#5A4479]/10 last:border-b-0 transition-colors ${rowClassName}`}
              //   onClick={() => onRowClick?.(row)}
              // >
              <tr
                key={row.organization_id || row.id || index}
                className={`border-b border-[#5A4479]/30 hover:bg-[#5A4479]/10 last:border-b-0 transition-colors ${rowClassName}`}
                onClick={(e) => {
                  // Prevent row click when clicking inside Action menu or button
                  if (e.target.closest('.action-dropdown-button') || e.target.closest('.action-dropdown-menu')) {
                    return;
                  }
                  onRowClick?.(row);
                }}
              >
                {columns.map(({ key, render }) =>
                  visibleColumns[key] && (
                    <td
                      key={key}
                      className={`
                        py-3 px-6 text-[#CCCCCCE5]
                        ${key === 'action'
                          ? 'overflow-visible relative z-10'
                          : 'truncate'
                        }
                      `}
                      title={
                        row[key] != null && String(row[key]).length > truncateLength
                          ? String(row[key])
                          : undefined
                      }
                    >
                      {render
                        ? render(row[key], row)
                        : row[key] == null
                        ? "NA"
                        : String(row[key]).length > truncateLength
                        ? String(row[key]).substring(0, truncateLength) + "..."
                        : row[key]}
                    </td>
                  )
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ReusableTable;